import { SectionFeaturedBlogPostsProps } from '@portfolio/models'
import { styled } from '@portfolio/styles'
import {
  Chip,
  Link,
  Media,
  SectionWrapper,
  isLinkHref,
} from '@portfolio/components'
import { useTranslation } from 'react-i18next'

const StyledSectionWrapper = styled(SectionWrapper, {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  paddingInline: 20,

  '@xl': {
    paddingInline: 30,
  },
})
const Heading = styled('h2', {
  projectFont: 'heading02',
  color: '$white',
  textAlign: 'center',
  marginBottom: '$96',

  '@xl': {
    marginBottom: '$120',
  },
})
const CardLink = styled(Link, {
  pointerEvents: 'all',
  zIndex: '$cardButton',
  width: 'max-content',
})
const CardsWrap = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: 30,
  marginBottom: '$40',

  '@xl': {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },

  '@media (min-width: 1522px)': {
    gridTemplateColumns: 'unset',
    gridAutoFlow: 'column',
    gridAutoColumns: 486,
  },
})

const Article = styled('div', {
  position: 'relative',
  width: '100%',
  color: '$white',
  height: '100%',
  display: 'grid',
  alignItems: 'start',
  gridTemplateRows: 'auto 1fr',
})

const ArticleTitle = styled('h3', {
  projectFont: 'heading07',
  gridRow: 2,
  gridColumn: 1,
  color: '$white',
})

const MediaWrap = styled('div', {
  gridColumn: 1,
  gridRow: 1,
  borderRadius: '$r2',
  overflow: 'hidden',
  transition: 'transform .5s ease',

  '& img': {
    transition: 'transform .5s ease',
  },

  '@md': {
    '&:hover': {
      '& img': {
        transform: 'scale(1.04)',
      },
    },
  },
})

const ArticleTags = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gridColumn: 1,
  gridRow: 1,
  columnGap: '$12',
})

const ArticleTag = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '$10',
})

const ArticleGridContent = styled('div', {
  position: 'relative',
  display: 'grid',
  rowGap: '$24',
  width: '100%',
  paddingTop: '$20',

  '@md': {
    rowGap: '$32',
    height: '100%',
    gridTemplateRows: 'auto auto 1fr',
  },
})

const StyledLink = styled(Link, {
  zIndex: '$content',
  projectFont: 'heading08',
  textDecoration: 'underline',
  gridColumn: 1,
  gridRow: 3,
  textUnderlineOffset: '.35em',
  color: '$white',
  display: 'grid',
  height: '100%',
  alignItems: 'end',
})

export const SectionFeaturedBlogPosts = ({
  title,
  featuredArticles,
  priority,
  ctaLabel,
  ctaLink,
  spacing,
  topBorder,
}: SectionFeaturedBlogPostsProps) => {
  const { t } = useTranslation('articles')
  const ctaHref = ctaLink && isLinkHref(ctaLink) && ctaLink.href

  return (
    <StyledSectionWrapper type={spacing || 'DEFAULT'} topBorder={topBorder}>
      {title && <Heading>{title}</Heading>}
      <CardsWrap>
        {featuredArticles?.map(({ id, link, image, title, tags }) => (
          <Article key={id}>
            {image && (
              <a href={link} tabIndex={-1} aria-label={`Article - ${title}`}>
                <MediaWrap>
                  <Media
                    {...image}
                    hardcropRatio={'landscape3'}
                    priority={priority}
                    sizes="(min-width: 740px) 50vw, 100vw"
                  />
                </MediaWrap>
              </a>
            )}
            <ArticleGridContent>
              {tags && (
                <ArticleTags>
                  <ArticleTag>
                    {tags.map((tag) => (
                      <Chip
                        key={tag._id}
                        title={tag.title}
                        type="light"
                        href={tag.path}
                      />
                    ))}
                  </ArticleTag>
                </ArticleTags>
              )}

              {title && <ArticleTitle>{title}</ArticleTitle>}

              {link && (
                <StyledLink href={link} appearance="text">
                  {t('readArticle')}
                </StyledLink>
              )}
            </ArticleGridContent>
          </Article>
        ))}
      </CardsWrap>
      {ctaHref && ctaLabel && (
        <CardLink appearance="containedTransparent" href={ctaHref}>
          {ctaLabel}
        </CardLink>
      )}
    </StyledSectionWrapper>
  )
}
